import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getReferenceValues,
  createReferenceValue,
  updateReferenceValue,
  deleteReferenceValue,
} from "../../../services/ReferenceValueService";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const emptyForm = {
  category: "",
  name: "",
  unit: "",
  normal_range_min: "",
  normal_range_max: "",
  normal_range_text: "",
  is_active: true,
};

const ReferenceValues = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.REFERENCE_VALUE_CREATE);
  const canUpdate = can(PERMISSIONS.REFERENCE_VALUE_UPDATE);
  const canDelete = can(PERMISSIONS.REFERENCE_VALUE_DELETE);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchValues = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "category", sort_dir: "asc" };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;

      const res = await getReferenceValues(params);
      const body = res.data;
      setValues(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.referenceValues.impossibleChargerValeursReference"));
      setValues([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, categoryFilter, t]);

  useEffect(() => {
    fetchValues();
  }, [fetchValues]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const categories = Array.from(new Set(values.map((v) => v.category).filter(Boolean)));

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setCategoryFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (rv) => {
    setFormMode("edit");
    setFormData({
      category: rv.category || "",
      name: rv.name || "",
      unit: rv.unit || "",
      normal_range_min: rv.normal_range_min ?? "",
      normal_range_max: rv.normal_range_max ?? "",
      normal_range_text: rv.normal_range_text || "",
      is_active: !!rv.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(rv.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = t("common.ui.leNomEstRequis");
    if (
      formData.normal_range_min === "" &&
      formData.normal_range_max === "" &&
      !formData.normal_range_text.trim()
    ) {
      errors.normal_range_text = t("pages.referenceValues.indiquezPlageNumeriqueMinMaxTexte");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        category: formData.category.trim() || null,
        name: formData.name.trim(),
        unit: formData.unit.trim() || null,
        normal_range_min: formData.normal_range_min === "" ? null : Number(formData.normal_range_min),
        normal_range_max: formData.normal_range_max === "" ? null : Number(formData.normal_range_max),
        normal_range_text: formData.normal_range_text.trim() || null,
        is_active: formData.is_active,
      };

      if (formMode === "add") {
        await createReferenceValue(payload);
        setFeedback({ type: "success", message: t("pages.referenceValues.valeurReferenceCreeeAvecSucces") });
      } else {
        await updateReferenceValue(editingId, payload);
        setFeedback({ type: "success", message: t("pages.referenceValues.valeurReferenceMiseJour") });
      }
      setShowFormModal(false);
      fetchValues();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteReferenceValue(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.referenceValues.valeurReferenceSupprimee") });
      setDeleteTarget(null);
      if (values.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchValues();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.referenceValues.impossibleSupprimerValeur") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const rangeDisplay = (rv) => {
    if (rv.normal_range_text) return rv.normal_range_text;
    if (rv.normal_range_min !== null && rv.normal_range_max !== null) {
      return `${rv.normal_range_min} – ${rv.normal_range_max}${rv.unit ? ` ${rv.unit}` : ""}`;
    }
    if (rv.normal_range_min !== null) return `≥ ${rv.normal_range_min}${rv.unit ? ` ${rv.unit}` : ""}`;
    if (rv.normal_range_max !== null) return `≤ ${rv.normal_range_max}${rv.unit ? ` ${rv.unit}` : ""}`;
    return "-";
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("pages.referenceValues.valeursReference")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" title={t("common.crud.refresh")} onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
<button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.referenceValues.ajouterValeur")}
              </button>
)}
            </div>
          </div>
        </div>

        <div className="alert alert-info">
          {t("pages.referenceValues.cesValeursBiologiquesReferencePeuventEtre")}
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.referenceValues.listeValeursReference")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.referenceValues.rechercherNom")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("pages.referenceValues.toutesCategories")}</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.ui.categorie")}</th>
                    <th>{t("common.fields.name")}</th>
                    <th>{t("pages.referenceValues.unite")}</th>
                    <th>{t("common.ui.valeursNormales")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : values.length > 0 ? (
                    values.map((rv) => (
                      <tr key={rv.id}>
                        <td>{rv.category || "-"}</td>
                        <td className="fw-medium">{rv.name}</td>
                        <td>{rv.unit || "-"}</td>
                        <td>{rangeDisplay(rv)}</td>
                        <td>
                          <span className={`badge badge-soft-${rv.is_active ? "success" : "danger"} d-inline-flex align-items-center`}>
                            <i className="ti ti-circle-filled fs-5 me-1"></i>
                            {rv.is_active ? t("common.ui.active") : t("common.ui.inactive")}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(rv)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(rv)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.referenceValues.aucuneValeurReferenceTrouvee")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-3 pt-3 border-top">
                <span className="text-sm text-gray-600">{t("pages.referenceValues.valeur", { total })}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    {t("common.ui.precedent")}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                    {t("common.ui.suivant")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.referenceValues.ajouterValeurReference") : t("pages.referenceValues.modifierValeurReference")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label">{t("common.ui.categorie")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.referenceValues.exBiochimie")}
                  value={formData.category}
                  onChange={(e) => handleFormChange("category", e.target.value)}
                />
              </div>
              <div className="col-6 mb-3">
                <label className="form-label">
                  {t("common.fields.name")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formErrors.name ? "is-invalid" : ""}`}
                  placeholder={t("pages.referenceValues.exCreatinineSerique")}
                  value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                />
                {formErrors.name && <div className="invalid-feedback d-block">{formErrors.name}</div>}
              </div>
            </div>
            <div className="row">
              <div className="col-4 mb-3">
                <label className="form-label">{t("pages.referenceValues.unite")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.referenceValues.exMg")}
                  value={formData.unit}
                  onChange={(e) => handleFormChange("unit", e.target.value)}
                />
              </div>
              <div className="col-4 mb-3">
                <label className="form-label">{t("pages.referenceValues.valeurMin")}</label>
                <input
                  type="number"
                  step="any"
                  className="form-control"
                  value={formData.normal_range_min}
                  onChange={(e) => handleFormChange("normal_range_min", e.target.value)}
                />
              </div>
              <div className="col-4 mb-3">
                <label className="form-label">{t("pages.referenceValues.valeurMax")}</label>
                <input
                  type="number"
                  step="any"
                  className="form-control"
                  value={formData.normal_range_max}
                  onChange={(e) => handleFormChange("normal_range_max", e.target.value)}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.referenceValues.ouValeurNonNumerique")}</label>
              <input
                type="text"
                className={`form-control ${formErrors.normal_range_text ? "is-invalid" : ""}`}
                placeholder={t("pages.referenceValues.exNegatif")}
                value={formData.normal_range_text}
                onChange={(e) => handleFormChange("normal_range_text", e.target.value)}
              />
              {formErrors.normal_range_text && (
                <div className="invalid-feedback d-block">{formErrors.normal_range_text}</div>
              )}
              <div className="form-text">{t("pages.referenceValues.renseignezMinMaxPourValeurNumerique")}</div>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="reference-value-active"
                checked={formData.is_active}
                onChange={(e) => handleFormChange("is_active", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="reference-value-active">
                {t("pages.referenceValues.valeurActive")}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            <Trans i18nKey="pages.referenceValues.voulezVousVraimentSupprimer" values={{ name: deleteTarget?.name }} components={[<strong />]} />
          </p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReferenceValues;
